import { useEffect } from 'react'

/**
 * App-wide "trading terminal" dark theme.
 *
 * Signed-in pages render inside Layout, which puts TERMINAL_THEME_CLASS on
 * <html> while it is mounted. Two things key off that class:
 *
 * - Tailwind `dark:` variants (see darkMode in tailwind.config.js), so shared
 *   components that already carry terminal styles for the Dashboard use them
 *   on every page.
 * - theme/terminal-theme.css, which remaps the light utility classes the
 *   pages are written in (bg-white, text-gray-900, border-gray-200, ...) onto
 *   the Dashboard's terminal palette.
 *
 * The class goes on <html> rather than a wrapper div so portalled UI
 * (tooltips, toasts) is themed too. In Layout it follows the user's
 * light/dark/auto choice (theme/themePreference.ts); PublicShell's public
 * record pages always pass true, so a shared link looks the same to everyone.
 * Sign-in pages render outside both and keep their own look.
 *
 * Reversible: build with REACT_APP_TERMINAL_THEME=off to get the light app
 * back (the theme choice is then hidden and the app is always light).
 */
export const TERMINAL_THEME_CLASS = 'theme-terminal'

/** The light palette's class (theme/light-theme.css), set by Layout in light. */
export const LIGHT_THEME_CLASS = 'theme-light'

export const isTerminalThemeEnabled = (): boolean =>
  process.env.REACT_APP_TERMINAL_THEME !== 'off'

/**
 * Apply the terminal theme to <html> while the caller is mounted and `active`
 * is true; switching `active` updates <html> at once, without a reload.
 */
export function useTerminalTheme(active = true): void {
  useEffect(() => {
    if (!active || !isTerminalThemeEnabled()) return undefined
    const root = document.documentElement
    root.classList.add(TERMINAL_THEME_CLASS)
    return () => root.classList.remove(TERMINAL_THEME_CLASS)
  }, [active])
}

/**
 * Apply the light palette to <html> while the caller is mounted and `active`
 * is true. Applies with the dark theme built out too: that app is light.
 */
export function useLightTheme(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined
    const root = document.documentElement
    root.classList.add(LIGHT_THEME_CLASS)
    return () => root.classList.remove(LIGHT_THEME_CLASS)
  }, [active])
}
